import type {ProjectPickerRelayProject as ProjectPickerProject} from '@github-ui/item-picker-project-relay'
import {useMemo} from 'react'

import type {IssueCreatePayload} from './model'
import {instanceOfIssueFormData} from './model'

export type ProjectInitializationOptions = {
  template?: IssueCreatePayload | null
  projects?: ProjectPickerProject[]
  isParentInheritance?: boolean
}

/**
 * Extracts projects from an issue template.
 */
function getProjectsFromTemplate(template: IssueCreatePayload): ProjectPickerProject[] {
  let initialProjects: ProjectPickerProject[] = []
  if (template?.data && instanceOfIssueFormData(template?.data)) {
    initialProjects = template.data.projects?.edges?.flatMap(e => (e?.node ? [e.node] : [])) || []
  }
  return initialProjects
}

/**
 * Hook for determining initial projects for issue creation based on template and parent inheritance.
 * This follows the same pattern as other metadata (labels, assignees, etc.) in the codebase.
 *
 * Priority logic:
 * 1. If not parent inheritance (URL params, Memex, etc.) - projects take highest precedence
 * 2. If parent inheritance + template projects exist - template projects override parent
 * 3. If parent inheritance + no template projects - use parent projects
 * 4. Empty array (final fallback)
 */
export function useInitialProjects(options: ProjectInitializationOptions): ProjectPickerProject[] {
  const {template, projects = [], isParentInheritance = false} = options

  return useMemo(() => {
    // If not parent inheritance (URL params, Memex, etc.), always use the provided projects
    if (!isParentInheritance) {
      return projects
    }

    // For parent inheritance, template projects take precedence
    const templateProjects = template ? getProjectsFromTemplate(template) : []
    if (templateProjects.length > 0) {
      return templateProjects
    }

    // Fall back to parent projects (inheritance)
    return projects
  }, [template, projects, isParentInheritance])
}
